<template>
  <div v-if="chartDataForScatter" class="x-section-wrapper">
    <ScatterChart
      v-bind="$attrs"
      :title="title"
      :aspect-ratio="1"
      draw-x-axis
      draw-y-axis
      draw-curve
      :data-sets="chartDataForScatter"
      :tick-frequency-x="tickSizeX"
      :tick-frequency-y="tickSizeY"
      :absolute-scale="true"
    >
      <template #background="{ xScaler, margins, domState, yScaler }">
        <rect
          v-if="domState.plotWidth > 0 && domState.plotHeight > 0"
          class="plotBg"
          :x="margins.left"
          :y="margins.top"
          :width="domState.plotWidth"
          :height="domState.plotHeight"
        />
        <template v-if="chartDataForScatter.every(cd => cd.points.length < 4)">
          <text
            x="50%"
            y="50%"
            dominant-baseline="middle"
            text-anchor="middle"
            fill="var(--plotColor0)"
            >No Data</text
          >
        </template>
        <template v-else-if="domState.plotWidth > 0 && domState.plotHeight > 0">
          <Axis
            :label="
              chartDataForScatter?.length > 0
                ? chartDataForScatter[0]?.yAxisLabel
                : undefined
            "
            :x="margins.left"
            :y="margins.top"
            :scaler="yScaler"
            :domain="adjustedDomainY"
            :plot-margin-px="6"
            :tick-frequency="tickSizeY"
            is-vertical
            draw-grid-lines
          />
          <Axis
            :label="
              chartDataForScatter?.length > 0
                ? chartDataForScatter[0]?.xAxisLabel
                : undefined
            "
            :x="margins.left"
            :y="margins.top"
            :scaler="xScaler"
            :domain="adjustedDomainX"
            :plot-margin-px="6"
            :tick-frequency="tickSizeX"
            draw-grid-lines
          />
        </template>
      </template>
      <template #perSeries="{ dataSeriesNumber, nearestPoint, screenPoints }">
        <LinePlot
          :screen-points="screenPoints"
          :data-series-number="dataSeriesNumber"
          :curve-interpolator="'straight'"
          :is-active="activeItems[dataSeriesNumber]"
        />

        <NearestPoint
          v-if="nearestPoint?.node"
          :data-series-number="dataSeriesNumber"
          :nearest-point="nearestPoint"
        />
      </template>

      <template
        v-if="chartDataForScatter.every(cd => cd.points.length < 4)"
        #crosshairs
      >
        Return none
      </template>
    </ScatterChart>
    <CustomLegend
      v-if="chartData"
      v-model="activeItems"
      :model="chartDataForScatter"
    >
      <template #label="{ index }">
        <div class="label-content">
          <div>
            {{ chartData[index]?.title.title || "" }}
          </div>
          <div class="value">
            {{ chartData[index]?.title.value || "" }}
          </div>
        </div>
      </template>
    </CustomLegend>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import type { DOMState } from "@incytestudios/ddv";
import {
  Axis,
  LinePlot,
  NearestPoint,
  useAggregateDomains,
  ScatterChart
} from "@incytestudios/ddv";
import type { CrossSectionDataSet } from "@/libs/charting/crossSection/d";
import type { ConvertableUnit } from "@/libs/units";
import CustomLegend from "./CustomLegend.vue";

// Define props for this component
const props = defineProps<{
  title: string;
  chartData: CrossSectionDataSet[];
  smallLengthUnit: ConvertableUnit;
}>();

// Convert CrossSectionDataSet to DataSet for ScatterChart
const chartDataForScatter = computed(() => {
  return props.chartData.map(item => ({
    ...item,
    title: item.title.title,
    points: item.points
  }));
});

// Keep original data for legend
const activeItems = ref(props.chartData.map(() => true));

const { aggregateX, aggregateY } = useAggregateDomains(
  chartDataForScatter,
  ref([]),
  ref(false)
);

const domState = ref<DOMState>();

const TARGET_TICKS_MAX = 10;
const TARGET_TICKS_MIN = 4;

const MIN_DIMENSION = 200;
const MAX_DIMENSION = 500;

function getTargetTicks(dimension: number | undefined): number {
  if (!dimension) return TARGET_TICKS_MIN; // fallback if dimension is undefined
  const clamped = Math.min(MAX_DIMENSION, Math.max(MIN_DIMENSION, dimension));
  const t = (clamped - MIN_DIMENSION) / (MAX_DIMENSION - MIN_DIMENSION);

  // Lerp between min and max ticks
  return Math.round(
    TARGET_TICKS_MIN + (TARGET_TICKS_MAX - TARGET_TICKS_MIN) * t
  );
}

const targetTicksX = computed(() => {
  return getTargetTicks(domState.value?.plotWidth);
});

const tickSizeX = computed(() => {
  if (!domState.value || !adjustedDomainX.value) return undefined;
  const plotWidth = domState.value.plotWidth * 0.9; // e.g., use 90% of the width, this is arbitrary but seems to work 100% of the time
  const pixelsPerTick = Math.floor(plotWidth / targetTicksX.value);
  return `${pixelsPerTick}px`;
});

const targetTicksY = computed(() => {
  return getTargetTicks(domState.value?.plotHeight);
});

const tickSizeY = computed(() => {
  if (!domState.value || !adjustedDomainY.value) return undefined;
  const plotHeight = domState.value.plotHeight * 0.9; // e.g., use 90% of the height, this is arbitrary but seems to work 100% of the time
  const pixelsPerTick = Math.floor(plotHeight / targetTicksY.value);
  return `${pixelsPerTick}px`;
});

// function to adjust the bounds of the chart so the target tick length
// can divide evenly into the range so ddv will actually draw the ticks
function adjustBounds(min: number, max: number, ticks: number) {
  const range = max - min;
  if (range <= 0) return { min, max };

  const idealStep = range / ticks;
  const magnitude = Math.pow(10, Math.floor(Math.log10(idealStep)));
  const niceStep = Math.ceil(idealStep / magnitude) * magnitude;

  const niceMin = Math.floor(min / niceStep) * niceStep;
  const niceMax = Math.ceil(max / niceStep) * niceStep;

  return { min: niceMin, max: niceMax };
}

const adjustedDomainX = computed(() => {
  if (!aggregateX.value) return { min: 0, max: 0 };
  const rawMin = aggregateX.value.min as number;
  const rawMax = aggregateX.value.max as number;
  return adjustBounds(rawMin, rawMax, targetTicksX.value);
});

const adjustedDomainY = computed(() => {
  if (!aggregateY.value) return { min: 0, max: 0 };
  const rawMin = aggregateY.value.min as number;
  const rawMax = aggregateY.value.max as number;
  return adjustBounds(rawMin, rawMax, targetTicksY.value);
});
</script>

<style lang="scss" scoped>
:deep(.axis .axis-label.isVertical) {
  transform: translate(20px, 0) rotate(-270deg) !important;
}

:deep(.title) {
  transform: translate(0, -5px) !important;
}

.x-section-wrapper {
  display: flex;

  :deep(.ddv.scatter) {
    flex: 1;
  }

  :deep(.custom-legend) {
    max-width: 200px;
    .control {
      font-size: 0.8rem !important;

      .label-content {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .value {
          font-family: var(--monospace-family) !important;
          opacity: 0.7;
          font-size: 0.7rem;
        }
      }
    }
  }
}
</style>
